import {
  AppModal,
  Button,
  Form,
  LoadingSpinner,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { SelectedFolderOrFile } from "@/components/ui/File/SelectedFolderOrFile";
import { SelectDestinationFolderSlideover } from "@/components/features/matter/file-repo/signature-requests/SelectDestinationFolderSlideover";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useDuplicateMatterFile } from "@/lib/data/react-query/matters/matter-file";
import { DuplicateMatterFileSchema } from "@/lib/data/schemas";
import type { MatterFile } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  buildUniqueFileName,
  getFileNameParts,
  getMatterFileNamesInFolder,
  matterFileNameExistsInFolder,
} from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { type z } from "zod";

interface DuplicateFileSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  sourceFile: MatterFile;
  onDuplicated?: () => void;
  onSaveAs?: (payload: { fileName: string; folderId: string }) => Promise<void>;
  title?: string;
  description?: string;
  submitLabel?: string;
  overlayClassName?: string;
  className?: string;
}

const formSchema = DuplicateMatterFileSchema.pick({
  name: true,
  idLocation: true,
});

const defaultValues: z.infer<typeof formSchema> = {
  name: "",
  idLocation: "",
};

export const DuplicateFileSlideover = ({
  open,
  setOpen,
  matterId,
  sourceFile,
  onDuplicated,
  onSaveAs,
  title,
  description,
  submitLabel,
  overlayClassName,
  className,
}: DuplicateFileSlideoverProps) => {
  const formId = "duplicate-file-form";
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onSubmit",
  });
  const {
    data: matterData,
    isLoading: isMatterLoading,
    isError: isMatterError,
  } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: open,
  });
  const matter = matterData?.data;
  const [selectedDestinationFolder, setSelectedDestinationFolder] =
    useState<MatterFile | null>(null);
  const [isSelectDestinationFolderOpen, setIsSelectDestinationFolderOpen] =
    useState(false);
  const duplicateMatterFile = useDuplicateMatterFile();
  const [isSaving, setIsSaving] = useState(false);
  const fileExtension = getFileNameParts(sourceFile.name).fileExtension;

  const handleDuplicate = async (data: z.infer<typeof formSchema>) => {
    if (!matter) {
      toast.error("Matter data is still loading.");
      return;
    }

    const fullFileName = fileExtension
      ? `${data.name}.${fileExtension}`
      : data.name;
    const destinationFolderId = data.idLocation ?? "";
    const duplicateName = matterFileNameExistsInFolder(
      matter.files,
      destinationFolderId,
      fullFileName,
    )
      ? buildUniqueFileName(
          fullFileName,
          getMatterFileNamesInFolder(matter.files, destinationFolderId),
        )
      : fullFileName;

    try {
      if (onSaveAs) {
        setIsSaving(true);
        await onSaveAs({
          fileName: duplicateName,
          folderId: destinationFolderId,
        });
      } else {
        await duplicateMatterFile.mutateAsync({
          matterId,
          id: sourceFile.id,
          name: duplicateName,
          idLocation: data.idLocation,
        });
      }
    } catch (error) {
      console.error("Failed to save file copy", error);
      toast.error("Failed to save copy. Please try again.");
      return;
    } finally {
      setIsSaving(false);
    }

    setOpen(false);
    onDuplicated?.();
  };

  const handleDestinationFolderSelected = (folder: MatterFile | null) => {
    setSelectedDestinationFolder(folder);
    form.setValue("idLocation", folder?.id ?? "", {
      shouldDirty: true,
    });
    setIsSelectDestinationFolderOpen(false);
  };

  const handleRemoveDestinationFolder = () => {
    setSelectedDestinationFolder(null);
    form.setValue("idLocation", "", { shouldDirty: true });
  };

  const selectedDestinationFolderParentPath = useMemo(() => {
    if (!selectedDestinationFolder) {
      return "";
    }

    const segments: string[] = [];
    let parentId = selectedDestinationFolder.idLocation;

    while (parentId) {
      const parentFolder = matter?.files.find(
        (file) => file.id === parentId && file.isFolder,
      );
      if (!parentFolder) {
        break;
      }
      segments.unshift(parentFolder.name);
      parentId = parentFolder.idLocation;
    }

    return segments.join(" / ");
  }, [matter?.files, selectedDestinationFolder]);

  useEffect(() => {
    if (!open) {
      form.reset(defaultValues);
      setSelectedDestinationFolder(null);
      setIsSelectDestinationFolderOpen(false);
      return;
    }
    if (!matter) {
      return;
    }

    const baseName = getFileNameParts(sourceFile.name).fileName;
    const destinationFolderId = sourceFile.idLocation ?? "";
    const suggestedFullName = buildUniqueFileName(
      fileExtension
        ? `${baseName ? `${baseName} copy` : "Copy"}.${fileExtension}`
        : baseName
          ? `${baseName} copy`
          : "Copy",
      getMatterFileNamesInFolder(matter.files, destinationFolderId),
    );
    const suggestedName = fileExtension
      ? getFileNameParts(suggestedFullName).fileName
      : suggestedFullName;
    form.reset({
      name: suggestedName,
      idLocation: destinationFolderId,
    });

    const destinationFolder = matter.files.find(
      (file) => file.id === destinationFolderId && file.isFolder,
    );
    setSelectedDestinationFolder(destinationFolder ?? null);
  }, [fileExtension, form, matter, open, sourceFile]);

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        overlayClassName={overlayClassName}
        className={className}
        title={title ?? "Duplicate file"}
        description={
          description ??
          "Choose a new name and destination folder for this file."
        }
        isDirty={form.formState.isDirty}
        loading={duplicateMatterFile.isPending || isSaving}
        size="lg"
        footer={
          !isMatterLoading && matter && !isMatterError ? (
            <div className="flex w-full justify-end gap-2">
              <FormCancelButton
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              <Button
                type="submit"
                form={formId}
                disabled={duplicateMatterFile.isPending || isSaving}
              >
                {submitLabel ?? (onSaveAs ? "Save copy" : "Save")}
              </Button>
            </div>
          ) : null
        }
      >
        {isMatterLoading && !matter ? (
          <div className="flex grow items-center justify-center">
            <LoadingSpinner className="h-6 w-6" />
          </div>
        ) : isMatterError || !matter ? (
          <div className="flex grow items-center justify-center px-6 py-8">
            <Typography className="text-destructive text-sm">
              Unable to load matter data for this file copy.
            </Typography>
          </div>
        ) : (
          <Form {...form}>
            <form
              id={formId}
              className="relative flex grow flex-col overflow-hidden"
              onSubmit={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await form.handleSubmit(handleDuplicate)();
              }}
            >
              {(duplicateMatterFile.isPending || isSaving) && (
                <FormLoadingOverlay />
              )}
              <div className="overflow-y-auto">
                <fieldset
                  className={cn(
                    slideoverFieldsetClassName,
                    "grid-cols-1 md:grid-cols-2",
                  )}
                >
                  <TextFormField
                    register={form.register("name")}
                    label="File name"
                    placeholder="Enter a file name"
                  />
                  {fileExtension ? (
                    <Typography variant="subHeading" className="mb-3 self-end">
                      .{fileExtension}
                    </Typography>
                  ) : (
                    <div />
                  )}
                  <div className="col-span-full space-y-3">
                    <div className="flex items-center justify-between">
                      <Typography className="text-sm font-medium">
                        Destination folder
                      </Typography>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsSelectDestinationFolderOpen(true);
                        }}
                      >
                        Select folder
                      </Button>
                    </div>
                    {selectedDestinationFolder ? (
                      <SelectedFolderOrFile
                        type="Folder"
                        name={selectedDestinationFolder.name}
                        parentPath={selectedDestinationFolderParentPath}
                        canRemove
                        handleRemoveSelectedFolder={
                          handleRemoveDestinationFolder
                        }
                      />
                    ) : (
                      <Typography className="text-muted-foreground text-sm">
                        Files will be saved to the root directory.
                      </Typography>
                    )}
                  </div>
                </fieldset>
              </div>
            </form>
          </Form>
        )}
      </AppModal>
      {matter ? (
        <SelectDestinationFolderSlideover
          open={isSelectDestinationFolderOpen}
          setOpen={setIsSelectDestinationFolderOpen}
          matter={matter}
          onFolderSelected={handleDestinationFolderSelected}
          title="Select destination folder"
          description="Choose a folder where the duplicated file will be stored"
        />
      ) : null}
    </>
  );
};
